/**
 * TipTap Spellcheck Extension
 *
 * Adds spellchecking decorations to the editor using nspell
 * with context menu support for spelling suggestions
 */

import { Extension } from '@tiptap/core';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { Decoration, DecorationSet } from '@tiptap/pm/view';
import { spellcheckService } from '../../services/spellcheckService';

export const SpellcheckPluginKey = new PluginKey('spellcheck');

/**
 * Extract words from text with their positions
 */
function extractWords(text, offset = 0) {
    const words = [];
    // Match words containing letters, hyphens, and apostrophes (numbers make words invalid)
    const wordRegex = /\b[a-zA-Z]+(?:['-]?[a-zA-Z0-9]+)*\b/g;
    let match;

    while ((match = wordRegex.exec(text)) !== null) {
        words.push({
            text: match[0],
            from: offset + match.index,
            to: offset + match.index + match[0].length
        });
    }

    return words;
}

/**
 * Find misspelled words in the document
 */
function findMisspelledWords(doc) {
    const misspelledWords = [];
    let totalWords = 0;

    doc.descendants((node, pos) => {
        if (node.isText && node.text) {
            const words = extractWords(node.text, pos);
            totalWords += words.length;

            for (const word of words) {
                const isCorrect = spellcheckService.correct(word.text);
                if (!isCorrect) {
                    misspelledWords.push({
                        from: word.from,
                        to: word.to,
                        word: word.text
                    });
                }
            }
        }
    });

    console.log(`[Spellcheck TipTap] findMisspelledWords: checked ${totalWords} words, found ${misspelledWords.length} misspelled`);
    return misspelledWords;
}

/**
 * Create decoration set from misspelled words
 */
function createDecorations(doc) {
    if (!spellcheckService.isInitialized()) {
        console.log('[Spellcheck TipTap] createDecorations: spellcheck not initialized yet');
        return DecorationSet.empty;
    }

    const misspelledWords = findMisspelledWords(doc);
    console.log(`[Spellcheck TipTap] createDecorations: found ${misspelledWords.length} misspelled words`);

    const decorations = misspelledWords.map(word =>
        Decoration.inline(word.from, word.to, {
            class: 'spellcheck-error',
            'data-word': word.word
        })
    );

    return DecorationSet.create(doc, decorations);
}

/**
 * Find misspelled word at a given position
 */
function findMisspelledWordAt(doc, pos) {
    let result = null;

    doc.descendants((node, nodePos) => {
        if (result) return false;

        if (node.isText && node.text) {
            const words = extractWords(node.text, nodePos);

            for (const word of words) {
                if (pos >= word.from && pos <= word.to) {
                    if (!spellcheckService.correct(word.text)) {
                        result = word;
                        return false;
                    }
                }
            }
        }
    });

    return result;
}

/**
 * Create and show context menu with spelling suggestions
 */
function showSpellingContextMenu(view, word, event) {
    event.preventDefault();

    // Remove any existing context menu
    const existingMenu = document.querySelector('.spellcheck-context-menu');
    if (existingMenu) {
        existingMenu.remove();
    }

    // Get suggestions
    const suggestions = spellcheckService.suggest(word.text);

    // Create context menu
    const menu = document.createElement('div');
    menu.className = 'spellcheck-context-menu';
    menu.style.position = 'fixed';
    menu.style.left = `${event.clientX}px`;
    menu.style.top = `${event.clientY}px`;
    menu.style.zIndex = '10000';
    menu.style.background = 'var(--color-surface-primary, #ffffff)';
    menu.style.border = '1px solid var(--color-border, #e0e0e0)';
    menu.style.borderRadius = 'var(--border-radius-md, 4px)';
    menu.style.boxShadow = 'var(--shadow-lg, 0 4px 12px rgba(0,0,0,0.15))';
    menu.style.minWidth = '150px';
    menu.style.maxWidth = '250px';
    menu.style.padding = '4px 0';

    // Add suggestions (max 5)
    const maxSuggestions = 5;
    const displaySuggestions = suggestions.slice(0, maxSuggestions);

    if (displaySuggestions.length > 0) {
        displaySuggestions.forEach(suggestion => {
            const item = document.createElement('div');
            item.className = 'spellcheck-menu-item';
            item.textContent = suggestion;
            item.style.padding = '6px 12px';
            item.style.cursor = 'pointer';
            item.style.fontSize = 'var(--font-size-sm, 14px)';
            item.style.color = 'var(--color-text-primary, #000000)';

            item.addEventListener('mouseenter', () => {
                item.style.background = 'var(--color-surface-hover, #f5f5f5)';
            });

            item.addEventListener('mouseleave', () => {
                item.style.background = 'transparent';
            });

            item.addEventListener('click', () => {
                // Replace the misspelled word with the suggestion
                const tr = view.state.tr.replaceWith(word.from, word.to,
                    view.state.schema.text(suggestion));
                view.dispatch(tr);
                menu.remove();
            });

            menu.appendChild(item);
        });

        // Add separator
        const separator = document.createElement('div');
        separator.style.height = '1px';
        separator.style.background = 'var(--color-border, #e0e0e0)';
        separator.style.margin = '4px 0';
        menu.appendChild(separator);
    } else {
        // No suggestions available
        const noSuggestions = document.createElement('div');
        noSuggestions.textContent = 'No suggestions';
        noSuggestions.style.padding = '6px 12px';
        noSuggestions.style.fontSize = 'var(--font-size-sm, 14px)';
        noSuggestions.style.color = 'var(--color-text-tertiary, #999999)';
        noSuggestions.style.fontStyle = 'italic';
        menu.appendChild(noSuggestions);

        const separator = document.createElement('div');
        separator.style.height = '1px';
        separator.style.background = 'var(--color-border, #e0e0e0)';
        separator.style.margin = '4px 0';
        menu.appendChild(separator);
    }

    // Add "Add to dictionary" option
    const addToDictionary = document.createElement('div');
    addToDictionary.className = 'spellcheck-menu-item';
    addToDictionary.textContent = 'Add to dictionary';
    addToDictionary.style.padding = '6px 12px';
    addToDictionary.style.cursor = 'pointer';
    addToDictionary.style.fontSize = 'var(--font-size-sm, 14px)';
    addToDictionary.style.color = 'var(--color-text-primary, #000000)';

    addToDictionary.addEventListener('mouseenter', () => {
        addToDictionary.style.background = 'var(--color-surface-hover, #f5f5f5)';
    });

    addToDictionary.addEventListener('mouseleave', () => {
        addToDictionary.style.background = 'transparent';
    });

    addToDictionary.addEventListener('click', () => {
        spellcheckService.addWord(word.text);
        // Force decoration recalculation
        const tr = view.state.tr;
        tr.setMeta('forceSpellcheckRefresh', true);
        view.dispatch(tr);
        menu.remove();
    });

    menu.appendChild(addToDictionary);

    // Add menu to document
    document.body.appendChild(menu);

    // Close menu when clicking outside
    const closeMenu = (e) => {
        if (!menu.contains(e.target)) {
            menu.remove();
            document.removeEventListener('mousedown', closeMenu);
            document.removeEventListener('contextmenu', closeMenu);
        }
    };

    setTimeout(() => {
        document.addEventListener('mousedown', closeMenu);
        document.addEventListener('contextmenu', closeMenu);
    }, 100);
}

/**
 * TipTap Spellcheck Extension
 */
export const Spellcheck = Extension.create({
    name: 'spellcheck',

    addOptions() {
        console.log('[Spellcheck TipTap] Extension.create() called');
        return {
            enabled: true
        };
    },

    onCreate() {
        console.log('[Spellcheck TipTap] onCreate() called');
    },

    addProseMirrorPlugins() {
        console.log('[Spellcheck TipTap] addProseMirrorPlugins() called');
        console.log('[Spellcheck TipTap] this.options:', this.options);
        const { enabled } = this.options;
        console.log('[Spellcheck TipTap] enabled:', enabled);

        if (!enabled) {
            console.log('[Spellcheck TipTap] Extension is disabled');
            return [];
        }

        let editorView = null;
        let checkInterval = null;

        console.log('[Spellcheck TipTap] Creating plugin');

        try {
            const plugin = new Plugin({
                key: SpellcheckPluginKey,
                state: {
                    init(_, { doc }) {
                        console.log('[Spellcheck TipTap] Plugin state.init() called');
                        return createDecorations(doc);
                    },
                    apply(tr, oldState) {
                        // Force refresh when technical dictionaries are loaded
                        if (tr.getMeta('forceSpellcheckRefresh')) {
                            console.log('[Spellcheck TipTap] apply: forceSpellcheckRefresh detected, recreating decorations');
                            return createDecorations(tr.doc);
                        }
                        // If document changed, recalculate decorations
                        if (tr.docChanged) {
                            console.log('[Spellcheck TipTap] apply: docChanged, recreating decorations');
                            return createDecorations(tr.doc);
                        }
                        // Otherwise, map decorations to new positions
                        return oldState.map(tr.mapping, tr.doc);
                    }
                },
                props: {
                    decorations(state) {
                        return this.getState(state);
                    },
                    handleDOMEvents: {
                        contextmenu(view, event) {
                            if (!spellcheckService.isInitialized()) {
                                return false;
                            }

                            const pos = view.posAtCoords({ left: event.clientX, top: event.clientY });
                            if (!pos) {
                                return false;
                            }

                            const word = findMisspelledWordAt(view.state.doc, pos.pos);
                            if (word) {
                                showSpellingContextMenu(view, word, event);
                                return true;
                            }

                            return false;
                        }
                    }
                },
                view(view) {
                    console.log('[Spellcheck TipTap] ========== view() called ==========');
                    console.log('[Spellcheck TipTap] spellcheckService:', spellcheckService);
                    console.log('[Spellcheck TipTap] spellcheckService.isReady:', spellcheckService.isReady);
                    console.log('[Spellcheck TipTap] spellcheckService.technicalDictsLoaded:', spellcheckService.technicalDictsLoaded);

                    editorView = view;

                    // Check immediately if dictionaries are already loaded
                    const isInit = spellcheckService.isInitialized();
                    console.log('[Spellcheck TipTap] Immediate check: isInitialized=', isInit);

                    // Force an immediate refresh regardless
                    console.log('[Spellcheck TipTap] Forcing immediate refresh in 100ms');
                    setTimeout(() => {
                        if (editorView) {
                            console.log('[Spellcheck TipTap] Executing immediate refresh');
                            const tr = editorView.state.tr;
                            tr.setMeta('forceSpellcheckRefresh', true);
                            editorView.dispatch(tr);
                            console.log('[Spellcheck TipTap] Immediate refresh dispatched');
                        }
                    }, 100);

                    // Also set up polling to catch late dictionary loads
                    console.log('[Spellcheck TipTap] Setting up polling interval');
                    checkInterval = setInterval(() => {
                        if (spellcheckService.isInitialized() && editorView) {
                            console.log('[Spellcheck TipTap] Polling detected initialization, refreshing');
                            clearInterval(checkInterval);
                            const tr = editorView.state.tr;
                            tr.setMeta('forceSpellcheckRefresh', true);
                            editorView.dispatch(tr);
                        }
                    }, 100);

                    // Stop polling after 3 seconds
                    setTimeout(() => {
                        console.log('[Spellcheck TipTap] Stopping polling after 3 seconds');
                        clearInterval(checkInterval);
                    }, 3000);

                    return {
                        destroy() {
                            console.log('[Spellcheck TipTap] Plugin view destroy() called');
                            if (checkInterval) {
                                clearInterval(checkInterval);
                            }
                            editorView = null;
                        }
                    };
                }
            });

            console.log('[Spellcheck TipTap] Plugin created successfully:', plugin);
            console.log('[Spellcheck TipTap] Plugin key:', plugin.spec.key);
            console.log('[Spellcheck TipTap] Returning plugin array');
            return [plugin];
        } catch (error) {
            console.error('[Spellcheck TipTap] Error creating plugin:', error);
            console.error('[Spellcheck TipTap] Error stack:', error.stack);
            return [];
        }
    }
});
